import { Badge, Flex, Stack, Text, Image } from '@mantine/core'
import dayjs from 'dayjs'
import { MRT_ColumnDef, MRT_PaginationState } from 'mantine-react-table'
import { useCallback, useEffect, useState } from 'react'
import {
	createBookUniVini,
	CreateBookUniViniPayload,
	deleteBookUniVini,
	getListBookUniVini,
	updateBookUniVini,
} from 'src/apis/bookAPI'
import BookCategory from 'src/components/CustomComponents/BookCategory'
import BookLanguage from 'src/components/CustomComponents/BookLanguage'
import { BOOK_LANGUAGE_OPTIONS } from 'src/components/CustomComponents/BookLanguage/BookLanguage'
import BookLanguageType from 'src/components/CustomComponents/BookLanguageType'
import BookLevel from 'src/components/CustomComponents/BookLevel'
import CustomButton from 'src/components/CustomComponents/CustomButton'
import CustomClearInput from 'src/components/CustomComponents/CustomClearInput'
import CustomDrawer from 'src/components/CustomComponents/CustomDrawer'
import CustomInput from 'src/components/CustomComponents/CustomInput'
import CustomModal from 'src/components/CustomComponents/CustomModal/CustomModal'
import CustomSelect from 'src/components/CustomComponents/CustomSelect'
import TableComponent from 'src/components/TableComponent'
import tableActionClasses from 'src/components/TableComponent/TableComponent.module.scss'
import { useLoading } from 'src/context/LoadingContext'
import { useModal } from 'src/context/ModalContext'
import { BookData, ChapterData } from 'src/interfaces'
import { color, formatDateTime } from 'src/variable/constant'
import { uploadProgress } from 'src/apis/uploadApi'
import { Dropzone, IMAGE_MIME_TYPE } from '@mantine/dropzone'
import CustomProgress from 'src/components/CustomComponents/CustomProgress'
import axios from 'axios'
import { IconHeadphones, IconPlus } from '@tabler/icons-react'
import CustomTooltip from 'src/components/CustomComponents/CustomTooltip'
import ChapterCreateDrawer from 'src/containers/BookUniVini/ChapterCreateDrawer'
import ChapterEditDrawer from 'src/containers/BookUniVini/ChapterEditDrawer'
import ChapterInfoEditDrawer from 'src/containers/BookUniVini/ChapterInfoEditDrawer'
import { useTabletLayout, useTouchDevice } from 'src/hooks/useTouchDevice'

interface BookUniViniPageListProps {
	columns: MRT_ColumnDef[]
	chapterColumns: MRT_ColumnDef[]
	[key: string]: any
}

type CreateBookForm = CreateBookUniViniPayload

const DEFAULT_CREATE_FORM: CreateBookForm = {
	title: '',
	language: BOOK_LANGUAGE_OPTIONS.map((item) => item.value),
	language_type: 'bilingual',
	category: 'Classics',
	author: 'UniVini Team',
	summary: '',
	level: 'a1',
	cover_image: '',
}

type DetailDrawerType = 'book' | 'chapter' | null
function BookUniViniPageList({ columns, chapterColumns }: BookUniViniPageListProps) {
	const { openError, openModal } = useModal()
	const { openLoadingContext, closeLoadingContext } = useLoading()
	const isTablet = useTabletLayout()
	const isTouchDevice = useTouchDevice()
	const [levelFilter, setLevelFilter] = useState<string | null>(null)
	const [categoryFilter, setCategoryFilter] = useState<string | null>(null)
	const [sortByFilter, setSortByFilter] = useState<string>('newest')
	const [searchKey, setSearchKey] = useState<string>('')
	const [data, setData] = useState<BookData[]>([])
	const [pagination, setPagination] = useState<MRT_PaginationState>({
		pageIndex: 1,
		pageSize: 10,
	})
	const [total, setTotal] = useState<number>(0)
	const [loading, setLoading] = useState<boolean>(false)
	const [createForm, setCreateForm] = useState<CreateBookForm>(DEFAULT_CREATE_FORM)
	const [creating, setCreating] = useState<boolean>(false)
	const [openCreateModal, setOpenCreateModal] = useState<boolean>(false)
	const [editingBookId, setEditingBookId] = useState<string | null>(null)
	const [openDetailDrawer, setOpenDetailDrawer] = useState<boolean>(false)
	const [detailDrawerType, setDetailDrawerType] = useState<DetailDrawerType>(null)
	const [selectedBook, setSelectedBook] = useState<BookData | null>(null)
	const [selectedChapter, setSelectedChapter] = useState<any>(null)
	const [openChapterCreateDrawer, setOpenChapterCreateDrawer] = useState<boolean>(false)
	const [createChapterBook, setCreateChapterBook] = useState<BookData | null>(null)
	const [openChapterEditDrawer, setOpenChapterEditDrawer] = useState<boolean>(false)
	const [openChapterInfoDrawer, setOpenChapterInfoDrawer] = useState<boolean>(false)
	const [editChapterBook, setEditChapterBook] = useState<BookData | null>(null)
	const [editChapter, setEditChapter] = useState<ChapterData | null>(null)

	const [uploadedFile, setUploadedFile] = useState<File | null>(null)
	const [localPreviewUrl, setLocalPreviewUrl] = useState<string | null>(null)
	const [progress, setProgress] = useState(0)
	const [controller, setController] = useState<AbortController | null>(null)

	const handleUploadImage = async (files: File[]) => {
		// console.log('Dropzone onDrop triggered. Files:', files)
		if (controller) {
			// console.log('Aborting previous API request')
			controller.abort() // Cancel previous API request
		}
		const newController = new AbortController()
		setController(newController)
		const signal = newController.signal
		const file = files[0]
		// console.log('Selected file for upload:', file)
		setUploadedFile(file)
		const objectUrl = URL.createObjectURL(file)
		// console.log('Created local preview URL:', objectUrl)
		setLocalPreviewUrl(objectUrl)

		try {
			// console.log('Starting uploadProgress API call')
			const uProgress = (progressEvent: ProgressEvent) => {
				const { loaded, total } = progressEvent
				const percentCompleted = Math.round((loaded * 100) / total)
				// console.log(`Upload progress: ${percentCompleted}%`)
				setProgress(percentCompleted)
			}
			const imageUrl = await uploadProgress(file, uProgress, signal)
			// console.log('Upload completed successfully. Image URL:', imageUrl)
			setTimeout(() => {
				// console.log('Setting createForm.cover_image to:', imageUrl)
				setCreateForm((prev) => ({ ...prev, cover_image: imageUrl }))
			}, 1000)
		} catch (error) {
			// console.error('Error during image upload:', error)
			if (axios.isCancel(error)) {
				// console.log('Upload canceled')
			} else {
				// console.log('Showing error modal:', error)
				openError(error)
			}
		}
	}

	const handleGetData = useCallback(async () => {
		let arr: BookData[] = []
		let count: number = 0
		setLoading(true)
		try {
			const res = await getListBookUniVini({
				// status: statusFilter,
				is_univini: true,
				// author: 'UniVini',
				level: levelFilter,
				category: categoryFilter,
				sortBy: sortByFilter,
				limit: pagination.pageSize,
				offset: (pagination.pageIndex - 1) * pagination.pageSize,
				...(searchKey && { search_text: searchKey }),
			})
			arr = res?.data?.results?.objects?.rows?.map((item, index) => ({
				...item,
				no: index + 1 + pagination.pageSize * (pagination.pageIndex - 1),
				created_at: dayjs(item?.created_at).format(formatDateTime),
				updated_at: dayjs(item?.updated_at).format(formatDateTime),
				image: item?.thumbnails?.[0] || '',
			}))
			count = res?.data?.results?.objects?.count
		} catch (error) {
			openError(error)
		} finally {
			setData(arr)
			setTotal(count)

			setTimeout(() => {
				setLoading(false)
			}, 500)
		}
	}, [pagination.pageIndex, pagination.pageSize, levelFilter, categoryFilter, sortByFilter, searchKey, openError])

	const handleChangePage = useCallback((value: number) => {
		setPagination((pre) => ({
			...pre,
			pageIndex: value,
		}))
	}, [])

	const handleViewDetail = useCallback(
		(row: any) => {
			setDetailDrawerType('chapter')
			setSelectedBook(null)
			setSelectedChapter(row?.original ?? null)
			setOpenDetailDrawer(true)
		},
		[],
	);

	const handleOpenCreateChapter = useCallback((book: BookData) => {
		setCreateChapterBook(book)
		setOpenChapterCreateDrawer(true)
	}, [])

	const handleEditChapterContent = useCallback((book: BookData, row: any) => {
		setEditChapterBook(book)
		setEditChapter(row?.original ?? null)
		setOpenChapterEditDrawer(true)
	}, [])

	const handleEditChapterInfo = useCallback((book: BookData, row: any) => {
		setEditChapterBook(book)
		setEditChapter(row?.original ?? null)
		setOpenChapterInfoDrawer(true)
	}, [])

	const handleViewBookDetail = useCallback((row: any) => {
		setDetailDrawerType('book')
		setSelectedChapter(null)
		setSelectedBook(row?.original ?? null)
		setOpenDetailDrawer(true)
	}, [])

	const handleCreateBook = useCallback(async () => {
		const title = createForm.title.trim()
		const category = createForm.category.trim()
		const author = createForm.author.trim()
		const summary = createForm.summary.trim()
		const level = createForm.level.trim()

		if (
			!title ||
			!createForm.language?.length ||
			!createForm.language_type ||
			!category ||
			!author ||
			!level
		) {
			// openError(new Error('Please input title, author, level, language, language type and category'))
			return
		}

		openLoadingContext()
		setCreating(true)
		try {
			const payload = {
				title,
				language: createForm.language,
				language_type: createForm.language_type,
				category,
				author,
				summary,
				level,
				cover_image: createForm.cover_image?.trim(),
			}

			if (editingBookId) {
				await updateBookUniVini(editingBookId, payload)
				openModal({ type: 'success', label: 'Update successfully !!!' })
			} else {
				await createBookUniVini(payload)
				openModal({ type: 'success', label: 'Create successfully !!!' })
			}

			setOpenCreateModal(false)
			setCreateForm(DEFAULT_CREATE_FORM)
			setEditingBookId(null)
			if (pagination.pageIndex === 1) {
				await handleGetData()
			} else {
				handleChangePage(1)
			}
		} catch (error) {
			openError(error)
		} finally {
			setCreating(false)
			closeLoadingContext()
		}
	}, [
		closeLoadingContext,
		createForm,
		editingBookId,
		handleChangePage,
		handleGetData,
		openError,
		openLoadingContext,
		openModal,
		pagination.pageIndex,
	])

	const handleEditBook = useCallback((row: any) => {
		const original = row?.original || {}
		setEditingBookId(original?.id || null)
		setCreateForm({
			title: original?.title || '',
			language:
				Array.isArray(original?.language) && original?.language.length
					? original.language
					: BOOK_LANGUAGE_OPTIONS.map((item) => item.value),
			language_type: original?.language_type || 'bilingual',
			category: Array.isArray(original?.category)
				? original?.category?.[0] || 'Classics'
				: original?.category || 'Classics',
			author: original?.author || 'UniVini Team',
			summary: original?.summary || '',
			level: original?.level || 'a1',
			cover_image: original?.cover_image || original?.thumbnails?.[0] || '',
		})
		setUploadedFile(null)
		setLocalPreviewUrl(null)
		setProgress(0)
		setOpenCreateModal(true)
	}, [])

	const handleDeleteBookRequest = useCallback(
		async (userBookId: string) => {
			openLoadingContext()
			try {
				await deleteBookUniVini(userBookId)
				if (pagination.pageIndex === 1) {
					await handleGetData()
				} else {
					handleChangePage(1)
				}
				openModal({
					type: 'success',
					label: 'Delete successfully !!!',
				})
			} catch (error) {
				openError(error)
			} finally {
				closeLoadingContext()
			}
		},
		[
			closeLoadingContext,
			handleChangePage,
			handleGetData,
			openError,
			openLoadingContext,
			openModal,
			pagination.pageIndex,
		],
	)

	const handleDeleteBook = useCallback(
		(row: any) => {
			openModal({
				type: 'confirm',
				label: <div>Do you want to delete this book?</div>,
				handleConfirm: () => handleDeleteBookRequest(row?.original?.id),
			})
		},
		[handleDeleteBookRequest, openModal],
	)

	useEffect(() => {
		handleGetData()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [JSON.stringify(pagination), levelFilter, categoryFilter, sortByFilter])

	useEffect(() => {
		let timer
		timer = setTimeout(() => {
			if (pagination.pageIndex === 1) {
				handleGetData()
			} else handleChangePage(1)
		}, 500)
		return () => clearTimeout(timer)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchKey])
	const detailDrawerTitle =
		detailDrawerType === 'book'
			? 'Book detail'
			: detailDrawerType === 'chapter'
				? 'Chapter detail'
				: 'Detail'

	return (
		<Stack sx={{ width: '100%' }}>
			<Flex
				gap={8}
				justify="space-between"
				sx={{
					flexWrap: isTablet ? 'nowrap' : 'wrap',
					alignItems: 'center',
					overflowX: isTablet ? 'auto' : 'visible',
					WebkitOverflowScrolling: 'touch',
					paddingBottom: isTablet ? 4 : 0,
				}}
			>
				<Flex
					gap={8}
					sx={{
						flexWrap: isTablet ? 'nowrap' : 'wrap',
						alignItems: 'center',
						minWidth: isTablet ? 'max-content' : undefined,
					}}
					className="items-center content-center"
				>
					<CustomClearInput
						value={searchKey}
						onChange={(e) => {
							setSearchKey(e)
							handleChangePage(1)
						}}
						placeholder={'Search'}
						sx={{ minWidth: 220 }}
					/>
					{/* <div>Status: </div>
					<CustomSelect
						value={statusFilter}
						data={[
							{ label: 'All', value: null}, 
							{ label: 'Inprogress', value: 'inprogress' }, { label: 'Approved', value: 'approved' }, { label: 'Rejected', value: 'rejected' }	]}
						onChange={(e) => {
							setStatusFilter(e)
						}}
						sx={{
							input: {
								color: 'black !important',
								fontWeight: 600,
							},
						}}
					/> */}
					<div>Level:</div>
					<BookLevel
						value={levelFilter}
						includeAll
						onChange={(e) => {
							setLevelFilter(e)
							handleChangePage(1)
						}}
						sx={{
							minWidth: 150,
							input: {
								color: 'black !important',
								fontWeight: 600,
							},
						}}
					/>
					<div>Category:</div>
					<BookCategory
						value={categoryFilter}
						includeAll
						includeAllLabel="All Categories"
						onChange={(value) => {
							setCategoryFilter(value as string | null)
							handleChangePage(1)
						}}
						sx={{
							minWidth: 170,
							input: {
								color: 'black !important',
								fontWeight: 600,
							},
						}}
					/>
					<div>Sort by:</div>
					<CustomSelect
						value={sortByFilter}
						data={[
							{ label: 'Newest', value: 'newest' },
							{ label: 'Oldest', value: 'oldest' },
							{ label: 'A-Z', value: 'a-z' },
							{ label: 'Z-A', value: 'z-a' },
						]}
						onChange={(value) => {
							setSortByFilter(value || 'newest')
							handleChangePage(1)
						}}
						sx={{ minWidth: 130 }}
					/>
				</Flex>
				<Flex
					gap={8}
					sx={{
						flexWrap: 'wrap',
						alignItems: 'center',
						marginLeft: isTablet ? 0 : 'auto',
						flexShrink: 0,
					}}
				>
					<div style={{ fontSize: 20, fontWeight: 600, color: color.CmsPrimary }}>
						Total: {total || ''}
					</div>
					<CustomButton
						variant="outlined"
						onClick={() => setOpenCreateModal(true)}
						sx={{
							height: isTouchDevice ? 44 : 36,
							whiteSpace: 'nowrap',
							minWidth: 120,
						}}
					>
						Create
					</CustomButton>
				</Flex>
			</Flex>
			<CustomModal size={'xl'} opened={openCreateModal} onClose={() => setOpenCreateModal(false)} title="Create Book UniVini">
				<Stack spacing={12} sx={{ padding: 20 }}>
					<Flex sx={{ fontSize: 22, fontWeight: 700 }} justify="center">
						{editingBookId ? 'Update Book UniVini' : 'Create Book UniVini'}
					</Flex>

					<Flex gap={12} sx={{ flexWrap: 'wrap' }}>
						<CustomInput
							label="Title"
							value={createForm.title}
							onChange={(e) =>
								setCreateForm((prev) => ({ ...prev, title: e.target.value }))
							}
							placeholder={'Title'}
							sx={{ flex: '1 1 320px', minWidth: 320 }}
						/>
						<CustomInput
							label="Author"
							value={createForm.author}
							// onChange={(e) =>
							// 	setCreateForm((prev) => ({ ...prev, author: e.target.value }))
							// }
							diasabled
							placeholder={'Author'}
							sx={{ flex: '1 1 320px', minWidth: 320 }}
						/>
					</Flex>

					<Flex gap={12} sx={{ flexWrap: 'wrap' }}>
						<BookLevel
							isUnivini
							value={createForm.level}
							onChange={(value) =>
								setCreateForm((prev) => ({
									...prev,
									level: value || 'a1',
								}))
							}
							sx={{
								flex: '1 1 320px',
								minWidth: 320,
								input: {
									color: 'black !important',
									fontWeight: 600,
								},
							}}
						/>
						<BookCategory
							value={createForm.category}
							onChange={(value) =>
								setCreateForm((prev) => ({
									...prev,
									category: (value || 'Classics') as string,
								}))
							}
							sx={{
								flex: '1 1 320px',
								minWidth: 320,
								input: {
									color: 'black !important',
									fontWeight: 600,
								},
							}}
						/>
					</Flex>

					<Flex gap={12} sx={{ flexWrap: 'wrap' }}>
						<BookLanguageType
							value={createForm.language_type}
							onChange={(value) =>
								setCreateForm((prev) => ({
									...prev,
									language_type: value || 'bilingual',
								}))
							}
							sx={{
								flex: '1 1 320px',
								minWidth: 320,
								input: {
									color: 'black !important',
									fontWeight: 600,
								},
							}}
						/>
						<BookLanguage
							multiple
							includeAll
							disabled
							value={createForm.language}
							onChange={(value) =>
								setCreateForm((prev) => ({
									...prev,
									language: value as string[],
								}))
							}
						// sx={{
						// 	flex: '1 1 320px',
						// 	minWidth: 320,
						// 	input: {
						// 		color: 'black !important',
						// 		fontWeight: 600,
						// 	},
						// }}
						/>
					</Flex>

					<CustomInput
						label="Summary"
						value={createForm.summary}
						onChange={(e) =>
							setCreateForm((prev) => ({ ...prev, summary: e.target.value }))
						}
						placeholder={'Summary'}
						multiline
						rows={4}
					/>
					<Stack spacing={8}>
						<label
							style={{
								display: 'inline-block',
								fontSize: '0.875rem',
								fontWeight: 500,
								color: '#212529',
							}}
						>
							Cover Image
						</label>
						{progress > 0 && progress < 100 && (
							<CustomProgress value={progress} />
						)}
						<Dropzone
							accept={IMAGE_MIME_TYPE}
							onDrop={handleUploadImage}
							multiple={false}
						>
							{createForm.cover_image || uploadedFile ? (
								<Image
									key={localPreviewUrl || createForm.cover_image}
									src={localPreviewUrl || createForm.cover_image}
									alt="Cover image"
									withPlaceholder
									sx={{ img: { maxHeight: 300 } }}
									fit="contain"
								/>
							) : (
								<div>Drag and drop a file or click to browse.</div>
							)}
						</Dropzone>
					</Stack>
					<Flex justify="flex-end" gap={8}>
						<CustomButton
							variant="outlined"
							onClick={() => {
								setOpenCreateModal(false)
								setCreateForm(DEFAULT_CREATE_FORM)
								setEditingBookId(null)
							}}
							sx={{ height: '36px', minWidth: 100 }}
						>
							Cancel
						</CustomButton>
						<CustomButton
							variant="outlined"
							onClick={handleCreateBook}
							disabled={creating}
							sx={{ height: '36px', minWidth: 120 }}
						>
							{editingBookId ? 'Update' : 'Create'}
						</CustomButton>
					</Flex>
				</Stack>
			</CustomModal>
			<CustomDrawer
				opened={openDetailDrawer}
				onClose={() => {
					setOpenDetailDrawer(false)
					setDetailDrawerType(null)
				}}
				title={detailDrawerTitle}
			>
				<Stack
					spacing={12}
					sx={{
						width: '100%',
						padding: 14,
						border: '1px solid #e6ebe7',
						borderRadius: 10,
					}}
				>
					{detailDrawerType === 'book' ? (
						<Stack spacing={12}>
							<Text size="sm" color="dimmed">
								Title
							</Text>
							<Text weight={600}>{selectedBook?.title || '-'}</Text>

							<Text size="sm" color="dimmed">
								Author
							</Text>
							<Text>{selectedBook?.author || '-'}</Text>

							<Text size="sm" color="dimmed">
								Level
							</Text>
							<Text>{selectedBook?.level || '-'}</Text>

							<Text size="sm" color="dimmed">
								Category
							</Text>
							<Text>{selectedBook?.category?.join(', ') || '-'}</Text>

							<Text size="sm" color="dimmed">
								Language
							</Text>
							<Text>{selectedBook?.language?.join(', ') || '-'}</Text>

							<Text size="sm" color="dimmed">
								Language Type
							</Text>
							<Text>{selectedBook?.language_type || '-'}</Text>

							<Text size="sm" color="dimmed">
								Summary
							</Text>
							<Text>{selectedBook?.summary || '-'}</Text>

							<Text size="sm" color="dimmed">
								Total Chapters
							</Text>
							<Text>{selectedBook?.chapters?.length ?? 0}</Text>
						</Stack>
					) : (
						<Stack spacing={12}>
							<Text size="sm" color="dimmed">
								Title
							</Text>
							<Text weight={600}>{selectedChapter?.title || '-'}</Text>

							<Text size="sm" color="dimmed">
								Description
							</Text>
							<Text>{selectedChapter?.description || '-'}</Text>

							<Text size="sm" color="dimmed">
								Status
							</Text>
							<Badge
								variant="light"
								color={
									String(selectedChapter?.approved_status || '').toLowerCase() ===
										'approved'
										? 'green'
										: String(selectedChapter?.approved_status || '').toLowerCase() ===
											'rejected'
											? 'red'
											: 'yellow'
								}
							>
								{selectedChapter?.approved_status || 'inprogress'}
							</Badge>

							<Text size="sm" color="dimmed">
								Original Doc Url
							</Text>
							{selectedChapter?.original_doc_url ? (
								<a
									href={selectedChapter?.original_doc_url}
									target="_blank"
									rel="noreferrer"
								>
									<Text>{selectedChapter?.original_doc_url}</Text>
								</a>
							) : (
								<Text>-</Text>
							)}

							<Text size="sm" color="dimmed">
								Audio Url
							</Text>
							{selectedChapter?.audio_url ? (
								<a href={selectedChapter?.audio_url} target="_blank" rel="noreferrer">
									<Text>{selectedChapter?.audio_url}</Text>
								</a>
							) : (
								<Text>-</Text>
							)}
						</Stack>
					)}
				</Stack>
			</CustomDrawer>
			<ChapterCreateDrawer
				opened={openChapterCreateDrawer}
				onClose={() => {
					setOpenChapterCreateDrawer(false)
					setCreateChapterBook(null)
				}}
				book={createChapterBook}
				onRefresh={handleGetData}
			/>
			<ChapterEditDrawer
				opened={openChapterEditDrawer}
				onClose={() => {
					setOpenChapterEditDrawer(false)
					setEditChapterBook(null)
					setEditChapter(null)
				}}
				book={editChapterBook}
				chapter={editChapter}
				onRefresh={handleGetData}
			/>
			<ChapterInfoEditDrawer
				opened={openChapterInfoDrawer}
				onClose={() => {
					setOpenChapterInfoDrawer(false)
					setEditChapterBook(null)
					setEditChapter(null)
				}}
				book={editChapterBook}
				chapter={editChapter}
				onRefresh={handleGetData}
			/>
			<TableComponent
				columns={columns}
				tableStyle="book"
				wrapperClassName={tableActionClasses.bookTableHost}
				total={total}
				data={data}
				pagination={pagination}
				loading={loading}
				handleChangePage={handleChangePage}
				lockActionColumn
				actionColumnSize={isTablet ? 176 : 148}
				// searchQueryValue={searchKey}
				// onSearchQueryInit={handleSearchQueryInit}
				// filterQueryValues={{
				// 	level: levelFilter || undefined,
				// 	category: categoryFilter || undefined,
				// 	sort_by: sortByFilter !== 'newest' ? sortByFilter : undefined,
				// }}
				// onFilterQueryInit={handleFilterQueryInit}
				handleDetail={handleViewBookDetail}
				detailActionLabel="View book"
				detailActionIcon="eye"
				hideMoreActions
				handleEdit={handleEditBook}
				editActionLabel="Edit book"
				handleDelete={handleDeleteBook}
				hasSubTable={() => true}
				_renderCustomActions={(row: any) => (
					<CustomTooltip withinPortal withArrow label="Add chapter">
						<button
							type="button"
							onClick={() => handleOpenCreateChapter(row.original)}
							className={`${tableActionClasses.actionButton} ${tableActionClasses.primaryAction}`}
						>
							<IconPlus size={16} />
						</button>
					</CustomTooltip>
				)}
				renderDetailPanel={({ row }) => {
					const chapters = row.original.chapters || []

					if (!chapters.length) {
						return (
							<div className={tableActionClasses.detailPanelHost}>
								<Flex justify="space-between" align="center" gap={8}>
									<Text size="sm" color="dimmed">
										No chapters yet
									</Text>
									<CustomButton
										variant="outlined"
										onClick={() => handleOpenCreateChapter(row.original)}
										sx={{
											height: isTouchDevice ? 44 : 32,
											minWidth: 120,
											fontSize: 12,
										}}
									>
										Add chapter
									</CustomButton>
								</Flex>
							</div>
						)
					}

					return (
						<div className={tableActionClasses.detailPanelHost}>
							<Flex justify="space-between" align="center" mb={8} gap={8}>
								<Text size="sm" weight={600} sx={{ color: '#374151' }}>
									{chapters.length} chapter{chapters.length === 1 ? '' : 's'}
								</Text>
								<CustomButton
									variant="outlined"
									onClick={() => handleOpenCreateChapter(row.original)}
									sx={{
										height: isTouchDevice ? 44 : 32,
										minWidth: 120,
										fontSize: 12,
									}}
								>
									Add chapter
								</CustomButton>
							</Flex>
							<TableComponent
								columns={chapterColumns}
								tableStyle="book"
								data={chapters}
								total={chapters.length}
								pagination={{ pageIndex: 1, pageSize: 100 }}
								loading={false}
								hideToolbar
								hidePagination
								hideMoreActions
								compact
								lockActionColumn
								actionColumnSize={isTablet ? 156 : 136}
								handleDetail={handleViewDetail}
								detailActionLabel="View chapter"
								detailActionIcon="eye"
								handleEdit={(chapterRow) =>
									handleEditChapterInfo(row.original, chapterRow)
								}
								editActionLabel="Edit info"
								_renderCustomActions={(chapterRow: any) => (
									<CustomTooltip withinPortal withArrow label="Edit content (audio/files)">
										<button
											type="button"
											onClick={() =>
												handleEditChapterContent(row.original, chapterRow)
											}
											className={`${tableActionClasses.actionButton} ${tableActionClasses.primaryAction}`}
										>
											<IconHeadphones size={16} />
										</button>
									</CustomTooltip>
								)}
								handleChangePage={function (value: number): void {
									console.log(value);
								}} />
						</div>
					)
				}}
			/>
		</Stack>
	)
}

export default BookUniViniPageList
